import { useEffect, useRef, useState } from "react";
import { XIcon, UploadIcon, TrashIcon } from "@heroicons/react/outline";

const EMPTY_FORM = {
  source_company: "",
  payee: "",
  invoice_no: "",
  amount: "",
  vat: "",
  currency: "EUR",
  payment_type: "",
  file: null,
  comments: "",
};

const inputCls =
  "w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-pink-500/40 transition";

const labelCls =
  "block text-xs uppercase tracking-wider text-slate-400 mb-1.5 font-medium";

const CompanyExpensesFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  initialData = null,
  payees = [],
  companies = [],
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm({
        source_company: initialData.source_company?.id || "",
        payee: initialData.payee?.id || "",
        invoice_no: initialData.invoice_no || "",
        amount: initialData.amount_no_vat ?? "",
        vat: initialData.vat ?? "",
        currency: initialData.currency || "EUR",
        payment_type: initialData.type_of_payment || "",
        comments: initialData.comments || "",
        file: null,
      });
    }
    if (mode === "create") {
      setForm(EMPTY_FORM);
    }
  }, [mode, initialData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFile = (file) => {
    if (!file) return;
    handleChange("file", file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  // Existing file URL from the server (edit mode only)
  const existingFileUrl = mode === "edit" ? initialData?.invoice_file : null;
  const existingFileName = existingFileUrl
    ? decodeURIComponent(existingFileUrl.split("/").pop().split("?")[0]) || "View file"
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-white">
              {mode === "edit" ? "Edit Expense" : "New Company Expense"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Log an outgoing payment or invoice
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition p-1 rounded-lg hover:bg-slate-800"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">

          {/* From / Payee row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Payment From*</label>
              <select
                value={form.source_company}
                onChange={(e) => handleChange("source_company", e.target.value)}
                className={inputCls}
              >
                <option value="" disabled>Select source…</option>
                {companies.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
                <option value="personal">Aaron Sevilla Personal</option>
              </select>
            </div>

            <div>
              <label className={labelCls}>Payee*</label>
              <select
                value={form.payee}
                onChange={(e) => handleChange("payee", e.target.value)}
                className={inputCls}
              >
                <option value="" disabled>Select payee…</option>
                {payees.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Invoice No */}
          <div>
            <label className={labelCls}>
              Invoice No{" "}
              <span className="text-slate-600 normal-case tracking-normal font-normal">
                (optional)
              </span>
            </label>
            <input
              type="text"
              value={form.invoice_no}
              onChange={(e) => handleChange("invoice_no", e.target.value)}
              placeholder="e.g. INV-2026-0042"
              className={inputCls}
            />
          </div>

          {/* Financials */}
          <div>
            <label className={labelCls}>Financials*</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Currency</p>
                <select
                  value={form.currency}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  className={inputCls}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="RON">RON</option>
                  <option value="AED">AED</option>
                </select>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Amount (ex. VAT)</p>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  placeholder="0.00"
                  className={inputCls}
                />
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">VAT</p>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.vat}
                  onChange={(e) => handleChange("vat", e.target.value)}
                  placeholder="0.00"
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          {/* Type of Payment */}
          <div>
            <label className={labelCls}>Type of Payment</label>
            <input
              type="text"
              value={form.payment_type}
              onChange={(e) => handleChange("payment_type", e.target.value)}
              placeholder="e.g. Bank Transfer, Contractor Fee, Venue Deposit…"
              className={inputCls}
            />
          </div>

          {/* File Upload */}
          <div>
            <label className={labelCls}>
              Invoice / Payment File{" "}
              <span className="text-slate-600 normal-case tracking-normal font-normal">
                (optional)
              </span>
            </label>

            <div className="space-y-2">

              {/* ── Existing file row (edit mode, no new file chosen yet) ── */}
              {existingFileUrl && !form.file && (
                <div className="flex items-center justify-between bg-slate-800/40 border border-white/10 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-md bg-slate-700 border border-white/10 flex items-center justify-center shrink-0">
                      <UploadIcon className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-500 mb-0.5 uppercase tracking-wider">Current file</p>
                      <a
                        href={existingFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-pink-400 hover:text-pink-300 underline underline-offset-2 truncate block max-w-[260px]"
                        title={existingFileName}
                      >
                        {existingFileName}
                      </a>
                    </div>
                  </div>
                  {/* Replace button */}
                  <button
                    type="button"
                    onClick={() => replaceInputRef.current?.click()}
                    className="ml-3 shrink-0 text-xs text-slate-400 hover:text-white border border-white/10 hover:border-white/25 px-3 py-1.5 rounded-md transition"
                  >
                    Replace
                  </button>
                  <input
                    ref={replaceInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
              )}

              {/* ── New file chosen preview ── */}
              {form.file && (
                <div className="flex items-center justify-between bg-slate-800/60 border border-white/10 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-md bg-pink-600/20 border border-pink-500/20 flex items-center justify-center shrink-0">
                      <UploadIcon className="w-4 h-4 text-pink-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-500 mb-0.5 uppercase tracking-wider">
                        {existingFileUrl ? "Replacing current file" : "Selected file"}
                      </p>
                      <p className="text-sm text-white truncate">{form.file.name}</p>
                      <p className="text-xs text-slate-500">{(form.file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleChange("file", null)}
                    className="text-slate-500 hover:text-red-400 transition ml-3 shrink-0"
                    title="Remove selected file"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* ── Drop zone (create mode, or edit mode with no existing file) ── */}
              {!existingFileUrl && !form.file && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`cursor-pointer border-2 border-dashed rounded-xl px-6 py-6 text-center transition
                    ${dragOver
                      ? "border-pink-500/60 bg-pink-500/5"
                      : "border-white/10 hover:border-white/20 hover:bg-slate-800/40"
                    }`}
                >
                  <UploadIcon className="w-6 h-6 text-slate-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">
                    Drop file here or{" "}
                    <span className="text-pink-400 underline underline-offset-2">browse</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-1">PDF, PNG, JPG up to 10 MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
              )}

            </div>
          </div>

          {/* Comments */}
          <div>
            <label className={labelCls}>Comments</label>
            <input
              type="text"
              value={form.comments}
              onChange={(e) => handleChange("comments", e.target.value)}
              className={inputCls}
            />
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 shrink-0">
          <p className="text-xs text-slate-600">* Required fields</p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`px-5 py-2 text-sm rounded-lg text-white font-medium transition
                ${loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
                }`}
            >
              {loading ? "Saving…" : mode === "edit" ? "Update Expense" : "Create Expense"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CompanyExpensesFormModal;